import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import {
  formatBytes,
  formatEta,
  formatSpeed,
  useUploadProgressState
} from '@services/uploadProgress/uploadProgress.store'

function UploadProgress({ sx }: { sx?: SxProps<Theme> }) {
  const progress = useUploadProgressState()

  if (!progress) {
    return null
  }

  const remainingBytes = Math.max(0, progress.total - progress.loaded)
  const etaSeconds =
    progress.phase === 'uploading' && progress.bytesPerSecond > 0
      ? remainingBytes / progress.bytesPerSecond
      : NaN

  const title =
    progress.totalFiles && progress.totalFiles > 1
      ? `Wysyłanie pliku ${progress.fileIndex ?? 1} z ${progress.totalFiles}`
      : 'Wysyłanie pliku'

  return (
    <Box
      sx={{
        width: '100%',
        my: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'grey.300',
        bgcolor: 'grey.50',
        ...sx
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
          <Typography variant='subtitle2' fontWeight={700}>
            {progress.phase === 'processing' ? 'Zapisywanie na serwerze…' : title}
          </Typography>
          {progress.totalFiles && progress.totalFiles > 1 && (
            <Typography variant='caption' color='text.secondary'>
              {progress.fileIndex}/{progress.totalFiles}
            </Typography>
          )}
        </Stack>

        {progress.fileName && (
          <Typography variant='caption' color='text.secondary' noWrap title={progress.fileName}>
            {progress.fileName}
          </Typography>
        )}

        <LinearProgress
          variant={progress.phase === 'processing' ? 'indeterminate' : 'determinate'}
          value={progress.percent}
          color={progress.slowConnection ? 'warning' : 'primary'}
          sx={{ height: 8, borderRadius: 1 }}
        />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          gap={0.5}
          flexWrap='wrap'
        >
          <Typography variant='body2' color='text.secondary'>
            {progress.phase === 'processing'
              ? '100%'
              : `${Math.round(progress.percent)}% · ${formatBytes(progress.loaded)} / ${formatBytes(progress.total)}`}
          </Typography>
          {progress.phase === 'uploading' && (
            <Typography variant='body2' color='text.secondary'>
              {formatSpeed(progress.bytesPerSecond)}
              {Number.isFinite(etaSeconds) ? ` · pozostało ${formatEta(etaSeconds)}` : ''}
            </Typography>
          )}
        </Stack>

        {progress.slowConnection && progress.phase === 'uploading' && (
          <Alert severity='warning' sx={{ py: 0.5 }}>
            Upload idzie wolno ({formatSpeed(progress.bytesPerSecond)}). To zwykle problem sieci na
            miejscu, nie błędu aplikacji.
          </Alert>
        )}
      </Stack>
    </Box>
  )
}

export default UploadProgress
